import { MessageCircle, Minimize2, Send, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getTemplateById } from '../../data/supportTemplates'
import BotAnimation from '../landing/BotAnimation'
import { API_URL } from '../../config/api'
import './SupportWidget.css'

interface Message {
  id: string
  type: 'user' | 'bot' | 'system'
  content: string
  timestamp: Date
  quickActions?: Array<{ label: string; templateId: string }>
}

export default function SupportWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [unreadCount, setUnreadCount] = useState(0)
  const [botMode, setBotMode] = useState<'happy' | 'sad' | 'surprised'>('happy')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Helper functions
  const getWelcomeQuickActions = useCallback(() => {
    return [
      { label: 'What is RunAI?', templateId: 'what-is-runai' },
      { label: 'How to start?', templateId: 'how-to-start' },
      { label: 'What is Agent?', templateId: 'what-is-agent' },
      { label: 'What is AiCloud?', templateId: 'what-is-aicloud' }
    ]
  }, [])

  const addBotMessage = useCallback((content: string, quickActions?: Array<{ label: string; templateId: string }>) => {
    setMessages(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      type: 'bot' as const,
      content,
      timestamp: new Date(),
      quickActions
    }])
  }, [])

  const addUserMessage = useCallback((content: string) => {
    setMessages(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      type: 'user' as const,
      content,
      timestamp: new Date()
    }])
  }, [])

  // Load messages from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('support_messages')
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Array<{
          id: string;
          type: string;
          content: string;
          timestamp: string;
          quickActions?: Array<{ label: string; templateId: string }>;
        }>;
        const loadedMessages: Message[] = parsed.map((m) => ({
          ...m,
          type: m.type as 'user' | 'bot' | 'system',
          timestamp: new Date(m.timestamp)
        }));
        setMessages(loadedMessages);
      } catch (e) {
        console.error('Failed to load messages:', e)
      }
    } else {
      // Welcome message - will be set on mount
      const welcomeActions = getWelcomeQuickActions();
      addBotMessage(
        'Hi! How can I help you with the RunAI platform?\n\nYou can select a category below or ask directly.',
        welcomeActions
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Save messages to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('support_messages', JSON.stringify(messages))
    }
  }, [messages])

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Update unread count
  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      if (lastMessage.type === 'bot') {
        setUnreadCount(prev => prev + 1)
      }
    } else {
      setUnreadCount(0)
    }
  }, [messages, isOpen])

  // Bot mode rotation (slow, like GIF)
  useEffect(() => {
    const interval = setInterval(() => {
      setBotMode(prev => {
        if (prev === 'happy') {
          return 'sad'
        }
        if (prev === 'sad') {
          return 'surprised'
        }
        return 'happy'
      })
    }, 6000) // 6 seconds per mode (slow)

    return () => clearInterval(interval)
  }, [])

  const handleQuickAction = (templateId: string) => {
    const template = getTemplateById(templateId)
    if (!template) {
      return
    }

    // Add user message
    addUserMessage(template.question)

    // Add bot response
    setTimeout(() => {
      if (template.answer) {
        const followUpActions = (template as any).followUpActions as Array<{ label: string; templateId: string }> | undefined
        addBotMessage(template.answer, followUpActions)
      }
    }, 500)
  }

  const handleSend = () => {
    if (!inputValue.trim()) {
      return
    }

    // Add user message
    addUserMessage(inputValue)
    setInputValue('')

    // Gerçek AI API'sine İstek Atıyoruz
    void (async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token') || ''
        const res = await fetch(`${API_URL}/api/ask-ai`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ query: inputValue })
        })
        if (!res.ok) {throw new Error('AskAI API hatası')}
        const data = await res.json()
        addBotMessage(data.answer || 'Cevap alınamadı.')
      } catch (e) {
        console.error(e)
        addBotMessage('Sisteme şu an bağlanamıyorum. Lütfen daha sonra tekrar deneyin.', getWelcomeQuickActions())
      }
    })()
  }

  // Clear chat function (currently unused but kept for future use)
  // const handleClearChat = () => {
  //   if (!window.confirm('Tum sohbet gecmisini silmek istediginizden emin misiniz?')) {
  //     return
  //   }
  //   setMessages([])
  //   localStorage.removeItem('support_messages')
  //   addBotMessage(
  //     'Sohbet gecmisi temizlendi. Size nasil yardimci olabilirim?',
  //     getWelcomeQuickActions()
  //   )
  // }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`support-widget-button bot-${botMode}`}
      >
        {/* Mini Bot - BotAnimation kucultulmus */}
        <div className="widget-bot-wrapper">
          <BotAnimation mode={botMode} />
        </div>
        {unreadCount > 0 && (
          <div className="unread-badge">
            {unreadCount}
          </div>
        )}
      </button>
    )
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      width: '380px',
      height: isMinimized ? '60px' : '600px',
      background: '#fff',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      overflow: 'hidden',
      transition: 'height 0.3s'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: isMinimized ? 'pointer' : 'default'
      }}
      onClick={() => {
        if (isMinimized) {
          setIsMinimized(false)
        }
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MessageCircle size={20} />
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700' }}>RunAI Support</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Her zaman yardima hazir</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsMinimized(!isMinimized)
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff'
            }}
          >
            <Minimize2 size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsOpen(false)
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff'
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {messages.map((message) => (
              <div key={message.id} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                gap: '8px'
              }}>
                <div style={{
                  maxWidth: '80%',
                  padding: '12px 16px',
                  borderRadius: message.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: message.type === 'user' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
                  color: message.type === 'user' ? '#fff' : '#1a202c',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {message.content}
                </div>
                {message.quickActions && message.quickActions.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    maxWidth: '80%'
                  }}>
                    {message.quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleQuickAction(action.templateId)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          color: '#667eea',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f8fafc'
                          e.currentTarget.style.borderColor = '#667eea'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff'
                          e.currentTarget.style.borderColor = '#e2e8f0'
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{
                  fontSize: '11px',
                  color: '#94a3b8'
                }}>
                  {message.timestamp.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: '12px'
          }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSend()
                }
              }}
              placeholder="Mesajinizi yazin..."
              style={{
                flex: 1,
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                background: inputValue.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e2e8f0',
                border: 'none',
                color: '#fff',
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Send size={18} />
            </button>
          </div>
        </>
      )}
    </div>
  )
}
